import dayjs from 'dayjs';
import { trimDecimals } from '@einhaus/meteoric-helpers';
import type { SearchSelectBadgeTone, SearchSelectItem } from '@/components/UI/SearchSelect';
import type { PerformanceDay, PerformanceStrategy, PerformanceStrategyDay, PerformanceTrade } from './performanceTypes';

export type PerformancePeriodKey = '1W' | '1M' | '3M' | 'YTD' | 'All';
/** `trading` removes external deposits and withdrawals from P&L; `funding` reports raw equity change. */
export type PerformanceBasis = 'trading' | 'funding';
/** `equity` colors a day by its mark-to-market change; `realized` by the P&L of trades closed that day. */
export type CalendarMode = 'equity' | 'realized';
export type BreakdownTab = 'month' | 'week' | 'weekday' | 'exitReason' | 'instrument';
/** The strategies a view is scoped to; null means the whole portfolio. */
export type StrategyScope = ReadonlySet<number> | null;

export const DATE_FORMAT = 'YYYY-MM-DD';

export const PERFORMANCE_PERIODS: readonly { key: PerformancePeriodKey; label: string; description: string }[] = [
    { key: '1W', label: '1W', description: 'Last 7 days' },
    { key: '1M', label: '1M', description: 'Last 30 days' },
    { key: '3M', label: '3M', description: 'Last 3 months' },
    { key: 'YTD', label: 'YTD', description: 'Year to date' },
    { key: 'All', label: 'All', description: 'Since inception' }
];

export const BREAKDOWN_TABS: readonly { key: BreakdownTab; label: string }[] = [
    { key: 'month', label: 'By month' },
    { key: 'week', label: 'By week' },
    { key: 'weekday', label: 'By weekday' },
    { key: 'exitReason', label: 'By exit reason' },
    { key: 'instrument', label: 'By instrument' }
];

const WEEKDAY_LABELS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export const isPerformancePeriodKey = (value: string | null | undefined): value is PerformancePeriodKey =>
    PERFORMANCE_PERIODS.some((period) => period.key === value);

export const isBreakdownTab = (value: string | null | undefined): value is BreakdownTab => BREAKDOWN_TABS.some((tab) => tab.key === value);

export const isCalendarMode = (value: string | null | undefined): value is CalendarMode => value === 'equity' || value === 'realized';

export const isPerformanceBasis = (value: string | null | undefined): value is PerformanceBasis =>
    value === 'trading' || value === 'funding';

export const isIsoDate = (value: string | null | undefined): value is string =>
    typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) && dayjs(value, DATE_FORMAT).isValid();

const round2 = (value: number) => trimDecimals(value, 2);

const sumBy = <T>(items: readonly T[], getValue: (item: T) => number) => items.reduce((total, item) => total + getValue(item), 0);

const groupBy = <T, K>(items: readonly T[], getKey: (item: T) => K): Map<K, T[]> => {
    const groups = new Map<K, T[]>();

    for (const item of items) {
        const key = getKey(item);
        const group = groups.get(key);

        if (group) group.push(item);
        else groups.set(key, [item]);
    }

    return groups;
};

/** Parses the comma-separated `strategy` URL parameter into known strategy ids, preserving order. */
export const parseStrategyScopeParam = (value: string | null | undefined, knownIds: ReadonlySet<number>): number[] => {
    if (!value) return [];
    const seen = new Set<number>();

    for (const token of value.split(',')) {
        if (!/^\d+$/.test(token)) continue;
        const id = Number(token);
        if (knownIds.has(id)) seen.add(id);
    }

    return [...seen];
};

export const scopeFromIds = (ids: readonly number[]): StrategyScope => (ids.length ? new Set(ids) : null);

export const isInScope = (scope: StrategyScope, strategyId: number) => scope === null || scope.has(strategyId);

/** Monday = 0 … Sunday = 6. */
export const getWeekdayIndex = (date: string) => (dayjs(date, DATE_FORMAT).day() + 6) % 7;

export const isWeekend = (date: string) => getWeekdayIndex(date) >= 5;

/** Monday of the week containing `date`. */
export const getWeekStartDate = (date: string) => dayjs(date, DATE_FORMAT).subtract(getWeekdayIndex(date), 'day').format(DATE_FORMAT);

export const getMonthKey = (date: string) => date.slice(0, 7);

export const shiftMonthKey = (monthKey: string, months: number) =>
    dayjs(`${monthKey}-01`, DATE_FORMAT).add(months, 'month').format('YYYY-MM');

export const formatMonthLabel = (monthKey: string, withYear = true) =>
    dayjs(`${monthKey}-01`, DATE_FORMAT).format(withYear ? 'MMMM YYYY' : 'MMM');

export const formatShortDate = (date: string) => dayjs(date, DATE_FORMAT).format('MMM D');

export const formatLongDate = (date: string) => dayjs(date, DATE_FORMAT).format('dddd, MMMM D, YYYY');

export const formatWeekdayDate = (date: string) => dayjs(date, DATE_FORMAT).format('ddd, MMM D');

export const getPeriodStartDate = (period: PerformancePeriodKey, today: string): string | null => {
    const reference = dayjs(today, DATE_FORMAT);

    switch (period) {
        case '1W':
            return reference.subtract(6, 'day').format(DATE_FORMAT);
        case '1M':
            return reference.subtract(30, 'day').format(DATE_FORMAT);
        case '3M':
            return reference.subtract(3, 'month').format(DATE_FORMAT);
        case 'YTD':
            return reference.startOf('year').format(DATE_FORMAT);
        case 'All':
            return null;
    }
};

export const filterDaysByPeriod = (days: readonly PerformanceDay[], startDate: string | null): PerformanceDay[] =>
    startDate === null ? [...days] : days.filter((day) => day.date >= startDate);

/** The day's per-strategy attribution rows that fall inside the scope (all of them for the whole portfolio). */
export const getScopedStrategyDays = (day: PerformanceDay, scope: StrategyScope): PerformanceStrategyDay[] =>
    scope === null ? day.strategies : day.strategies.filter((strategy) => scope.has(strategy.strategyId));

/** Day P&L for the whole portfolio on the chosen basis, or the scoped strategies' realized + mark-to-market attribution. */
export const getDayPnl = (day: PerformanceDay, basis: PerformanceBasis, scope: StrategyScope): number => {
    if (scope !== null) {
        return round2(sumBy(getScopedStrategyDays(day, scope), (strategy) => strategy.realizedPnl + strategy.markToMarketPnl));
    }

    return basis === 'funding' ? day.totalPnl : day.adjustedTotalPnl;
};

export const getDayRealizedPnl = (day: PerformanceDay, scope: StrategyScope): number =>
    scope === null ? day.realizedPnl : round2(sumBy(getScopedStrategyDays(day, scope), (strategy) => strategy.realizedPnl));

export const getDayClosedCount = (day: PerformanceDay, scope: StrategyScope): number =>
    scope === null ? day.closedTradeCount : sumBy(getScopedStrategyDays(day, scope), (strategy) => strategy.closedTradeCount);

export const getDayOpenedCount = (day: PerformanceDay, scope: StrategyScope): number =>
    scope === null ? day.openedTradeCount : sumBy(getScopedStrategyDays(day, scope), (strategy) => strategy.openedTradeCount);

export const getCalendarValue = (day: PerformanceDay, mode: CalendarMode, basis: PerformanceBasis, scope: StrategyScope): number =>
    mode === 'realized' ? getDayRealizedPnl(day, scope) : getDayPnl(day, basis, scope);

/** A day counts as active when money moved or a trade opened or closed. Weekends and holidays are never special-cased. */
export const isActiveDay = (day: PerformanceDay, basis: PerformanceBasis, scope: StrategyScope): boolean =>
    getDayPnl(day, basis, scope) !== 0 || getDayClosedCount(day, scope) > 0 || getDayOpenedCount(day, scope) > 0;

export const tradeMatchesScope = (trade: PerformanceTrade, scope: StrategyScope) => isInScope(scope, trade.strategyId);

export const getTradeHoldDays = (trade: Pick<PerformanceTrade, 'entryDate' | 'exitDate'>, today: string): number =>
    dayjs(trade.exitDate ?? today, DATE_FORMAT).diff(dayjs(trade.entryDate, DATE_FORMAT), 'day');

export const isClosedTrade = (trade: PerformanceTrade): trade is PerformanceTrade & { profit: number; exitDate: string } =>
    trade.status === 'closed' && trade.profit !== null && trade.exitDate !== null;

export interface TradeStats {
    tradeCount: number;
    winCount: number;
    lossCount: number;
    winRate: number | null;
    grossProfit: number;
    grossLoss: number;
    netProfit: number;
    /** Gross profit ÷ gross loss; Infinity when there are wins and no losses; null without trades. */
    profitFactor: number | null;
    averageProfitPercentage: number | null;
    averageHoldDays: number | null;
    bestTrade: PerformanceTrade | null;
    worstTrade: PerformanceTrade | null;
}

export const computeTradeStats = (trades: readonly PerformanceTrade[], today: string): TradeStats => {
    const closed = trades.filter(isClosedTrade);
    const tradeCount = closed.length;

    if (tradeCount === 0) {
        return {
            tradeCount: 0,
            winCount: 0,
            lossCount: 0,
            winRate: null,
            grossProfit: 0,
            grossLoss: 0,
            netProfit: 0,
            profitFactor: null,
            averageProfitPercentage: null,
            averageHoldDays: null,
            bestTrade: null,
            worstTrade: null
        };
    }

    const wins = closed.filter((trade) => trade.profit > 0);
    const losses = closed.filter((trade) => trade.profit < 0);
    const grossProfit = round2(sumBy(wins, (trade) => trade.profit));
    const grossLoss = round2(-sumBy(losses, (trade) => trade.profit));

    let profitFactor: number | null = null;
    if (grossLoss > 0) profitFactor = round2(grossProfit / grossLoss);
    else if (grossProfit > 0) profitFactor = Infinity;

    const percentages = closed.map((trade) => trade.profitPercentage).filter((value): value is number => value !== null);

    return {
        tradeCount,
        winCount: wins.length,
        lossCount: losses.length,
        winRate: round2((wins.length / tradeCount) * 100),
        grossProfit,
        grossLoss,
        netProfit: round2(sumBy(closed, (trade) => trade.profit)),
        profitFactor,
        averageProfitPercentage: percentages.length ? round2(sumBy(percentages, (value) => value) / percentages.length) : null,
        averageHoldDays: round2(sumBy(closed, (trade) => getTradeHoldDays(trade, today)) / tradeCount),
        bestTrade: closed.reduce((best, trade) => (trade.profit > best.profit ? trade : best)),
        worstTrade: closed.reduce((worst, trade) => (trade.profit < worst.profit ? trade : worst))
    };
};

export interface EquityStats {
    pnl: number;
    /** P&L relative to the equity at the start of the period; null without a positive starting equity. */
    returnPercent: number | null;
    startEquity: number | null;
    endEquity: number | null;
    activeDays: number;
    greenDays: number;
    redDays: number;
    /** Largest peak-to-trough decline, as a negative percent of the peak (portfolio) or of starting equity (scoped). */
    maxDrawdownPercent: number;
    maxDrawdownAmount: number;
    drawdownPeakDate: string | null;
    drawdownTroughDate: string | null;
    bestDay: PerformanceDay | null;
    worstDay: PerformanceDay | null;
    bestDayPnl: number | null;
    worstDayPnl: number | null;
}

/** First day of the range that had positive equity at its open: the base for percent returns. */
export const getPeriodStartEquity = (days: readonly PerformanceDay[]): number | null => {
    const day = days.find((candidate) => candidate.startingEquity > 0);
    return day ? day.startingEquity : null;
};

export const computeEquityStats = (days: readonly PerformanceDay[], basis: PerformanceBasis, scope: StrategyScope): EquityStats => {
    const pnl = round2(sumBy(days, (day) => getDayPnl(day, basis, scope)));
    const startEquity = getPeriodStartEquity(days);
    const endEquity = days.length ? days[days.length - 1].endingEquity : null;

    let peak = scope === null ? (startEquity ?? 0) : 0;
    let peakDate: string | null = days[0]?.date ?? null;
    let cumulative = 0;
    let maxDrawdownPercent = 0;
    let maxDrawdownAmount = 0;
    let drawdownPeakDate: string | null = null;
    let drawdownTroughDate: string | null = null;

    for (const day of days) {
        cumulative = round2(cumulative + getDayPnl(day, basis, scope));
        const value = scope === null ? (basis === 'funding' ? day.endingEquity : (startEquity ?? 0) + cumulative) : cumulative;

        if (value > peak) {
            peak = value;
            peakDate = day.date;
        }

        const drawdownAmount = round2(value - peak);
        const base = scope === null ? peak : startEquity;
        const drawdownPercent = base && base > 0 ? round2((drawdownAmount / base) * 100) : 0;

        if (drawdownAmount < maxDrawdownAmount) {
            maxDrawdownAmount = drawdownAmount;
            maxDrawdownPercent = drawdownPercent;
            drawdownPeakDate = peakDate;
            drawdownTroughDate = day.date;
        }
    }

    const movedDays = days.filter((day) => getDayPnl(day, basis, scope) !== 0);
    const bestDay = movedDays.length
        ? movedDays.reduce((best, day) => (getDayPnl(day, basis, scope) > getDayPnl(best, basis, scope) ? day : best))
        : null;
    const worstDay = movedDays.length
        ? movedDays.reduce((worst, day) => (getDayPnl(day, basis, scope) < getDayPnl(worst, basis, scope) ? day : worst))
        : null;

    return {
        pnl,
        returnPercent: startEquity ? round2((pnl / startEquity) * 100) : null,
        startEquity,
        endEquity,
        activeDays: days.filter((day) => isActiveDay(day, basis, scope)).length,
        greenDays: days.filter((day) => getDayPnl(day, basis, scope) > 0).length,
        redDays: days.filter((day) => getDayPnl(day, basis, scope) < 0).length,
        maxDrawdownPercent,
        maxDrawdownAmount,
        drawdownPeakDate,
        drawdownTroughDate,
        bestDay,
        worstDay,
        bestDayPnl: bestDay ? getDayPnl(bestDay, basis, scope) : null,
        worstDayPnl: worstDay ? getDayPnl(worstDay, basis, scope) : null
    };
};

export interface EquityCurvePoint {
    date: string;
    /** Portfolio equity, or cumulative scoped P&L when strategies are selected. */
    value: number;
    /** Drawdown from the running peak as a negative percent; null when no base exists. */
    drawdownPercent: number | null;
    dayPnl: number;
    realizedPnl: number;
    closedCount: number;
    fundingFlow: number;
}

export const buildEquityCurve = (days: readonly PerformanceDay[], basis: PerformanceBasis, scope: StrategyScope): EquityCurvePoint[] => {
    const startEquity = getPeriodStartEquity(days);
    let cumulative = 0;
    let peak = scope === null ? (startEquity ?? 0) : 0;

    return days.map((day) => {
        const dayPnl = getDayPnl(day, basis, scope);
        cumulative = round2(cumulative + dayPnl);
        const value = scope === null ? (basis === 'funding' ? day.endingEquity : (startEquity ?? 0) + cumulative) : cumulative;
        if (value > peak) peak = value;
        const base = scope === null ? peak : startEquity;

        return {
            date: day.date,
            value,
            drawdownPercent: base && base > 0 ? round2(((value - peak) / base) * 100) : null,
            dayPnl,
            realizedPnl: getDayRealizedPnl(day, scope),
            closedCount: getDayClosedCount(day, scope),
            fundingFlow: day.fundingFlow
        };
    });
};

export interface CalendarCell {
    date: string;
    dayOfMonth: number;
    /** Monday = 0 … Sunday = 6. */
    weekdayIndex: number;
    hasData: boolean;
    isFuture: boolean;
    isToday: boolean;
    value: number;
    isFlat: boolean;
    closedCount: number;
    openedCount: number;
    closedStrategyIds: number[];
    hasFunding: boolean;
}

export interface CalendarWeek {
    weekStartDate: string;
    cells: (CalendarCell | null)[];
    total: number;
    closedCount: number;
}

export interface CalendarMonth {
    monthKey: string;
    label: string;
    weeks: CalendarWeek[];
    total: number;
    returnPercent: number | null;
    activeDays: number;
    /** Largest absolute cell value in the month; drives heatmap intensity. */
    scale: number;
}

export const buildCalendarMonth = (config: {
    dayByDate: ReadonlyMap<string, PerformanceDay>;
    trades: readonly PerformanceTrade[];
    monthKey: string;
    mode: CalendarMode;
    basis: PerformanceBasis;
    scope: StrategyScope;
    today: string;
}): CalendarMonth => {
    const { dayByDate, trades, monthKey, mode, basis, scope, today } = config;
    const monthStart = dayjs(`${monthKey}-01`, DATE_FORMAT);
    const daysInMonth = monthStart.daysInMonth();
    const closedStrategyIdsByDate = new Map<string, number[]>();

    for (const trade of trades) {
        if (!isClosedTrade(trade) || !tradeMatchesScope(trade, scope) || getMonthKey(trade.exitDate) !== monthKey) continue;
        const ids = closedStrategyIdsByDate.get(trade.exitDate) ?? [];
        if (!ids.includes(trade.strategyId)) ids.push(trade.strategyId);
        closedStrategyIdsByDate.set(trade.exitDate, ids);
    }

    const cells: CalendarCell[] = [];

    for (let dayOfMonth = 1; dayOfMonth <= daysInMonth; dayOfMonth++) {
        const date = monthStart.date(dayOfMonth).format(DATE_FORMAT);
        const day = dayByDate.get(date);
        const value = day ? getCalendarValue(day, mode, basis, scope) : 0;
        const closedCount = day ? getDayClosedCount(day, scope) : 0;
        const openedCount = day ? getDayOpenedCount(day, scope) : 0;
        const hasFunding = day !== undefined && scope === null && day.fundingFlow !== 0;

        cells.push({
            date,
            dayOfMonth,
            weekdayIndex: getWeekdayIndex(date),
            hasData: day !== undefined,
            isFuture: date > today,
            isToday: date === today,
            value,
            isFlat: value === 0 && closedCount === 0 && openedCount === 0 && !hasFunding,
            closedCount,
            openedCount,
            closedStrategyIds: closedStrategyIdsByDate.get(date) ?? [],
            hasFunding
        });
    }

    const weeks: CalendarWeek[] = [];
    let current: (CalendarCell | null)[] = Array.from({ length: cells[0]?.weekdayIndex ?? 0 }, () => null);

    const flush = () => {
        while (current.length < 7) current.push(null);
        const present = current.filter((cell): cell is CalendarCell => cell !== null);
        const firstCell = present[0];
        if (!firstCell) return;

        weeks.push({
            weekStartDate: getWeekStartDate(firstCell.date),
            cells: current,
            total: round2(sumBy(present, (cell) => cell.value)),
            closedCount: sumBy(present, (cell) => cell.closedCount)
        });
        current = [];
    };

    for (const cell of cells) {
        current.push(cell);
        if (cell.weekdayIndex === 6) flush();
    }

    if (current.length) flush();

    const monthDays = cells.map((cell) => dayByDate.get(cell.date)).filter((day): day is PerformanceDay => day !== undefined);
    const total = round2(sumBy(cells, (cell) => cell.value));
    const startEquity = mode === 'equity' && scope === null ? getPeriodStartEquity(monthDays) : null;

    return {
        monthKey,
        label: formatMonthLabel(monthKey),
        weeks,
        total,
        returnPercent: startEquity ? round2((total / startEquity) * 100) : null,
        activeDays: cells.filter((cell) => !cell.isFlat).length,
        scale: Math.max(0, ...cells.map((cell) => Math.abs(cell.value)))
    };
};

export interface YearStripMonth {
    monthKey: string;
    label: string;
    hasData: boolean;
    total: number;
    returnPercent: number | null;
}

export const buildYearStrip = (config: {
    days: readonly PerformanceDay[];
    year: number;
    mode: CalendarMode;
    basis: PerformanceBasis;
    scope: StrategyScope;
}): YearStripMonth[] => {
    const { days, year, mode, basis, scope } = config;
    const daysByMonth = groupBy(
        days.filter((day) => day.date.startsWith(`${year}-`)),
        (day) => getMonthKey(day.date)
    );

    return Array.from({ length: 12 }, (_, index) => {
        const monthKey = `${year}-${String(index + 1).padStart(2, '0')}`;
        const monthDays = daysByMonth.get(monthKey) ?? [];
        const total = round2(sumBy(monthDays, (day) => getCalendarValue(day, mode, basis, scope)));
        const startEquity = mode === 'equity' && scope === null ? getPeriodStartEquity(monthDays) : null;

        return {
            monthKey,
            label: formatMonthLabel(monthKey, false),
            hasData: monthDays.length > 0,
            total,
            returnPercent: startEquity ? round2((total / startEquity) * 100) : null
        };
    });
};

export interface DayDetail {
    day: PerformanceDay;
    closed: PerformanceTrade[];
    opened: PerformanceTrade[];
    /** Trades holding a position at the end of the day (opened on or before, not yet closed). */
    held: PerformanceTrade[];
}

export const getDayDetail = (day: PerformanceDay, trades: readonly PerformanceTrade[], scope: StrategyScope): DayDetail => {
    const scoped = trades.filter((trade) => tradeMatchesScope(trade, scope));

    return {
        day,
        closed: scoped.filter((trade) => trade.exitDate === day.date),
        opened: scoped.filter((trade) => trade.entryDate === day.date),
        held: scoped.filter((trade) => trade.entryDate <= day.date && (trade.exitDate === null || trade.exitDate > day.date))
    };
};

export type StrategyStatus = 'active' | 'paused' | 'unsubscribed';

export const STRATEGY_STATUS_LABELS: Readonly<Record<StrategyStatus, string>> = {
    active: 'Active',
    paused: 'Paused',
    unsubscribed: 'Unsubscribed'
};

export const STRATEGY_STATUS_TONES: Readonly<Record<StrategyStatus, SearchSelectBadgeTone>> = {
    active: 'success',
    paused: 'warning',
    unsubscribed: 'neutral'
};

/** Active: subscribed and able to open entries. Paused: subscribed but switched off. Unsubscribed: traded before, no longer in the portfolio. */
export const getStrategyStatus = (strategy: Pick<PerformanceStrategy, 'isSubscribed' | 'effectiveActive'>): StrategyStatus => {
    if (!strategy.isSubscribed) return 'unsubscribed';
    return strategy.effectiveActive ? 'active' : 'paused';
};

export interface StrategyAttributionRow {
    strategyId: number;
    name: string;
    isSubscribed: boolean;
    effectiveActive: boolean;
    status: StrategyStatus;
    closedCount: number;
    openCount: number;
    /** Executed trades across all time, regardless of the selected period. */
    lifetimeTradeCount: number;
    winRate: number | null;
    realizedPnl: number;
    /** Sum of open-position P&L from the latest marks; null when no open trade has a mark yet. */
    openPnl: number | null;
    totalPnl: number;
    /** Realized plus mark-to-market attribution across the period's days. */
    periodPnl: number;
    profitFactor: number | null;
    averageProfitPercentage: number | null;
    averageHoldDays: number | null;
    lastActivityDate: string | null;
    /** Cumulative period P&L per day, for a sparkline. */
    cumulativePeriodPnl: number[];
}

export const buildStrategyRows = (config: {
    strategies: readonly PerformanceStrategy[];
    trades: readonly PerformanceTrade[];
    days: readonly PerformanceDay[];
    periodStart: string | null;
    today: string;
}): StrategyAttributionRow[] => {
    const { strategies, trades, days, periodStart, today } = config;
    const tradesByStrategy = groupBy(trades, (trade) => trade.strategyId);

    return strategies.map((strategy) => {
        const strategyTrades = tradesByStrategy.get(strategy.strategyId) ?? [];
        const closedInPeriod = strategyTrades.filter(
            (trade) => isClosedTrade(trade) && (periodStart === null || trade.exitDate >= periodStart)
        );
        const open = strategyTrades.filter((trade) => trade.status === 'open');
        const openMarks = open.map((trade) => trade.unrealizedPnl).filter((value): value is number => value !== null);
        const stats = computeTradeStats(closedInPeriod, today);
        const scope = new Set([strategy.strategyId]);

        let cumulative = 0;
        const cumulativePeriodPnl = days.map((day) => {
            cumulative = round2(cumulative + getDayPnl(day, 'trading', scope));
            return cumulative;
        });

        const activityDates = strategyTrades.map((trade) => trade.exitDate ?? trade.entryDate);
        const openPnl = openMarks.length ? round2(sumBy(openMarks, (value) => value)) : null;

        return {
            strategyId: strategy.strategyId,
            name: strategy.name,
            isSubscribed: strategy.isSubscribed,
            effectiveActive: strategy.effectiveActive,
            status: getStrategyStatus(strategy),
            closedCount: stats.tradeCount,
            openCount: open.length,
            lifetimeTradeCount: strategyTrades.length,
            winRate: stats.winRate,
            realizedPnl: stats.netProfit,
            openPnl,
            totalPnl: round2(stats.netProfit + (openPnl ?? 0)),
            periodPnl: cumulativePeriodPnl.length ? cumulativePeriodPnl[cumulativePeriodPnl.length - 1] : 0,
            profitFactor: stats.profitFactor,
            averageProfitPercentage: stats.averageProfitPercentage,
            averageHoldDays: stats.averageHoldDays,
            lastActivityDate: activityDates.length ? activityDates.reduce((latest, date) => (date > latest ? date : latest)) : null,
            cumulativePeriodPnl
        };
    });
};

/**
 * How a strategy relates to the selected period, in display order. Everything but `noTrades` is relevant;
 * `noTrades` rows exist only because the portfolio subscribes to the strategy.
 */
export type StrategyActivityGroup = 'tradedInPeriod' | 'openPositions' | 'tradedEarlier' | 'unsubscribed' | 'noTrades';

export const STRATEGY_ACTIVITY_GROUP_ORDER: readonly StrategyActivityGroup[] = [
    'tradedInPeriod',
    'openPositions',
    'tradedEarlier',
    'unsubscribed',
    'noTrades'
];

export const STRATEGY_ACTIVITY_GROUP_LABELS: Readonly<Record<StrategyActivityGroup, string>> = {
    tradedInPeriod: 'Traded in this period',
    openPositions: 'Open positions',
    tradedEarlier: 'Traded before this period',
    unsubscribed: 'No longer subscribed',
    noTrades: 'Subscribed, no trades yet'
};

export const getStrategyActivityGroup = (
    row: Pick<StrategyAttributionRow, 'closedCount' | 'openCount' | 'lifetimeTradeCount' | 'isSubscribed'>
): StrategyActivityGroup => {
    if (row.closedCount > 0) return 'tradedInPeriod';
    if (row.openCount > 0) return 'openPositions';
    if (!row.isSubscribed) return 'unsubscribed';
    if (row.lifetimeTradeCount > 0) return 'tradedEarlier';
    return 'noTrades';
};

export const isDormantStrategyRow = (
    row: Pick<StrategyAttributionRow, 'closedCount' | 'openCount' | 'lifetimeTradeCount' | 'isSubscribed'>
) => getStrategyActivityGroup(row) === 'noTrades';

/** Activity group order, then most recent activity first, then name. */
export const sortStrategyRows = (rows: readonly StrategyAttributionRow[]): StrategyAttributionRow[] =>
    [...rows].sort((first, second) => {
        const groupDelta =
            STRATEGY_ACTIVITY_GROUP_ORDER.indexOf(getStrategyActivityGroup(first)) -
            STRATEGY_ACTIVITY_GROUP_ORDER.indexOf(getStrategyActivityGroup(second));
        if (groupDelta !== 0) return groupDelta;

        const activityDelta = (second.lastActivityDate ?? '').localeCompare(first.lastActivityDate ?? '');
        if (activityDelta !== 0) return activityDelta;

        return first.name.localeCompare(second.name) || first.strategyId - second.strategyId;
    });

/** One-line activity summary for a strategy row, used under the name in pickers and tables. */
export const describeStrategyActivity = (row: StrategyAttributionRow): string => {
    switch (getStrategyActivityGroup(row)) {
        case 'tradedInPeriod':
            return `${row.closedCount} closed${row.openCount ? ` · ${row.openCount} open` : ''}${row.lastActivityDate ? ` · last ${formatShortDate(row.lastActivityDate)}` : ''}`;
        case 'openPositions':
            return `${row.openCount} open position${row.openCount === 1 ? '' : 's'} · none closed in period`;
        case 'tradedEarlier':
            return `${row.lifetimeTradeCount} lifetime trade${row.lifetimeTradeCount === 1 ? '' : 's'}${row.lastActivityDate ? ` · last ${formatShortDate(row.lastActivityDate)}` : ''} · none in period`;
        case 'unsubscribed':
            return `${row.lifetimeTradeCount} lifetime trade${row.lifetimeTradeCount === 1 ? '' : 's'}${row.lastActivityDate ? ` · last ${formatShortDate(row.lastActivityDate)}` : ''}`;
        case 'noTrades':
            return 'No trades yet';
    }
};

/** Typeahead items for the strategy scope picker: grouped by activity, badged by status, searchable by name, id, status and group. */
export const buildStrategyScopeItems = (rows: readonly StrategyAttributionRow[]): SearchSelectItem[] =>
    sortStrategyRows(rows).map((row) => {
        const group = getStrategyActivityGroup(row);
        const statusLabel = STRATEGY_STATUS_LABELS[row.status];

        return {
            value: row.strategyId,
            name: row.name,
            label: `${row.name} (#${row.strategyId}), ${statusLabel.toLowerCase()}`,
            meta: describeStrategyActivity(row),
            badge: statusLabel,
            badgeTone: STRATEGY_STATUS_TONES[row.status],
            group: STRATEGY_ACTIVITY_GROUP_LABELS[group],
            searchText: `${statusLabel} ${STRATEGY_ACTIVITY_GROUP_LABELS[group]} ${row.name}`.toLowerCase()
        };
    });

export interface PeriodBreakdownRow {
    key: string;
    label: string;
    pnl: number;
    returnPercent: number | null;
    activeDays: number;
    greenDays: number;
    redDays: number;
    closedCount: number;
    winRate: number | null;
    realizedPnl: number;
}

export const buildPeriodBreakdownRows = (config: {
    days: readonly PerformanceDay[];
    trades: readonly PerformanceTrade[];
    tab: 'month' | 'week' | 'weekday';
    basis: PerformanceBasis;
    scope: StrategyScope;
    today: string;
}): PeriodBreakdownRow[] => {
    const { days, trades, tab, basis, scope, today } = config;

    const keyFor = (date: string) => {
        if (tab === 'month') return getMonthKey(date);
        if (tab === 'week') return getWeekStartDate(date);
        return String(getWeekdayIndex(date));
    };

    const labelFor = (key: string) => {
        if (tab === 'month') return formatMonthLabel(key);
        if (tab === 'week') return `Week of ${formatShortDate(key)}`;
        return WEEKDAY_LABELS[Number(key)] ?? key;
    };

    const daysByKey = groupBy(days, (day) => keyFor(day.date));
    const closedTrades = trades.filter((trade) => isClosedTrade(trade) && tradeMatchesScope(trade, scope));
    const dayDates = new Set(days.map((day) => day.date));
    const tradesByKey = groupBy(
        closedTrades.filter((trade) => dayDates.has(trade.exitDate as string)),
        (trade) => keyFor(trade.exitDate as string)
    );

    const keys = tab === 'weekday' ? ['0', '1', '2', '3', '4', '5', '6'].filter((key) => daysByKey.has(key)) : [...daysByKey.keys()].sort();

    return keys.map((key) => {
        const groupDays = daysByKey.get(key) ?? [];
        const stats = computeTradeStats(tradesByKey.get(key) ?? [], today);
        const pnl = round2(sumBy(groupDays, (day) => getDayPnl(day, basis, scope)));
        const startEquity = scope === null && tab !== 'weekday' ? getPeriodStartEquity(groupDays) : null;

        return {
            key,
            label: labelFor(key),
            pnl,
            returnPercent: startEquity ? round2((pnl / startEquity) * 100) : null,
            activeDays: groupDays.filter((day) => isActiveDay(day, basis, scope)).length,
            greenDays: groupDays.filter((day) => getDayPnl(day, basis, scope) > 0).length,
            redDays: groupDays.filter((day) => getDayPnl(day, basis, scope) < 0).length,
            closedCount: stats.tradeCount,
            winRate: stats.winRate,
            realizedPnl: stats.netProfit
        };
    });
};

export interface TradeBreakdownRow {
    key: string;
    label: string;
    tradeCount: number;
    winRate: number | null;
    netProfit: number;
    /** Share of the absolute net profit across all groups, as a percent. */
    sharePercent: number | null;
    averageProfitPercentage: number | null;
    averageHoldDays: number | null;
    profitFactor: number | null;
    bestTrade: PerformanceTrade | null;
    worstTrade: PerformanceTrade | null;
}

const EXIT_REASON_LABELS: Readonly<Record<string, string>> = {
    manualSell: 'Manual close',
    optionExpiry: 'Closed at expiry',
    optionExpiryAutoSell: 'Closed at expiry',
    optionExpiryAutoBuy: 'Closed at expiry',
    expiration: 'Expired worthless',
    stopLoss: 'Stop loss',
    underlyingStopLoss: 'Underlying stop loss',
    sellAtTarget: 'Target reached',
    underlyingTarget: 'Underlying target',
    profitTarget: 'Profit target',
    profitTaking: 'Profit taking',
    profitFloor: 'Profit floor',
    closeAtProfitFloor: 'Profit floor',
    closeFromProfitTakingRules: 'Profit taking',
    sellAtClose: 'Closed at session end',
    closeAtTime: 'Closed at time',
    sellAtWeekendClose: 'Closed before weekend',
    maximumHoldMinutes: 'Max hold time',
    maximumTradingHoldMinutes: 'Max hold time',
    maxHoldBars: 'Max hold bars',
    maximumNonProfitableHoldMinutes: 'Max unprofitable hold',
    closeBeforeEarnings: 'Closed before earnings',
    closeBeforeFomc: 'Closed before FOMC',
    sellAbovePrice: 'Price level',
    sellBelowPrice: 'Price level',
    sellAtLevel: 'Price level',
    indicator: 'Indicator exit',
    automatedExit: 'Automated exit',
    brokerClose: 'Broker close',
    dataError: 'Data error',
    optionChainMissing: 'Option chain missing',
    endOfBars: 'End of data'
};

export const getExitReasonLabel = (exitReason: string | null): string => {
    if (exitReason === null) return 'Unspecified';
    return EXIT_REASON_LABELS[exitReason] ?? exitReason.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (letter) => letter.toUpperCase());
};

export const getInstrumentLabel = (
    trade: Pick<PerformanceTrade, 'derivativeType' | 'openingTransactionType' | 'entityType' | 'direction'>
): string => {
    if (trade.derivativeType === 'Option') return trade.openingTransactionType === 'sellToOpen' ? 'Short option' : 'Long option';
    if (trade.derivativeType === 'Future') return 'Future';
    if (trade.entityType === 'Cryptocurrency') return 'Crypto';
    if (trade.entityType === 'AssetIndex') return 'Index';
    return trade.direction === 'short' ? 'Short stock' : 'Stock';
};

export const buildTradeBreakdownRows = (config: {
    trades: readonly PerformanceTrade[];
    tab: 'exitReason' | 'instrument';
    scope: StrategyScope;
    today: string;
}): TradeBreakdownRow[] => {
    const { trades, tab, scope, today } = config;
    const closed = trades.filter((trade) => isClosedTrade(trade) && tradeMatchesScope(trade, scope));
    const groups = groupBy(closed, (trade) => (tab === 'exitReason' ? (trade.exitReason ?? 'unspecified') : getInstrumentLabel(trade)));
    const totalAbsolute = sumBy(closed, (trade) => Math.abs(trade.profit ?? 0));

    return [...groups.entries()]
        .map(([key, groupTrades]) => {
            const stats = computeTradeStats(groupTrades, today);

            return {
                key,
                label: tab === 'exitReason' ? getExitReasonLabel(key === 'unspecified' ? null : key) : key,
                tradeCount: stats.tradeCount,
                winRate: stats.winRate,
                netProfit: stats.netProfit,
                sharePercent: totalAbsolute > 0 ? round2((Math.abs(stats.netProfit) / totalAbsolute) * 100) : null,
                averageProfitPercentage: stats.averageProfitPercentage,
                averageHoldDays: stats.averageHoldDays,
                profitFactor: stats.profitFactor,
                bestTrade: stats.bestTrade,
                worstTrade: stats.worstTrade
            };
        })
        .sort((first, second) => second.netProfit - first.netProfit);
};

export interface LedgerRow {
    date: string;
    startingEquity: number;
    endingEquity: number;
    fundingFlow: number;
    pnl: number;
    returnPercent: number | null;
    closedCount: number;
    realizedPnl: number;
    cumulativePnl: number;
}

export const buildLedgerRows = (config: {
    days: readonly PerformanceDay[];
    basis: PerformanceBasis;
    scope: StrategyScope;
    hideFlatDays: boolean;
}): LedgerRow[] => {
    const { days, basis, scope, hideFlatDays } = config;
    let cumulative = 0;
    const rows: LedgerRow[] = [];

    for (const day of days) {
        const pnl = getDayPnl(day, basis, scope);
        cumulative = round2(cumulative + pnl);

        if (hideFlatDays && !isActiveDay(day, basis, scope) && day.fundingFlow === 0) continue;

        rows.push({
            date: day.date,
            startingEquity: day.startingEquity,
            endingEquity: day.endingEquity,
            fundingFlow: day.fundingFlow,
            pnl,
            returnPercent: day.startingEquity > 0 ? round2((pnl / day.startingEquity) * 100) : null,
            closedCount: getDayClosedCount(day, scope),
            realizedPnl: getDayRealizedPnl(day, scope),
            cumulativePnl: cumulative
        });
    }

    return rows.reverse();
};

const csvCell = (value: string | number | null) => {
    if (value === null) return '';
    const text = String(value);
    return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export const buildLedgerCsv = (rows: readonly LedgerRow[]): string => {
    const header = [
        'Date',
        'Starting equity',
        'Ending equity',
        'Funding flow',
        'P&L',
        'Return %',
        'Closed trades',
        'Realized P&L',
        'Cumulative P&L'
    ];
    const lines = [...rows]
        .reverse()
        .map((row) =>
            [
                row.date,
                row.startingEquity,
                row.endingEquity,
                row.fundingFlow,
                row.pnl,
                row.returnPercent,
                row.closedCount,
                row.realizedPnl,
                row.cumulativePnl
            ]
                .map(csvCell)
                .join(',')
        );

    return [header.join(','), ...lines].join('\n');
};

/** Validated categorical palette (dark surfaces); strategies past the eighth share a neutral so hues are never recycled. */
export const STRATEGY_COLOR_PALETTE: readonly string[] = [
    '#3987e5',
    '#d95926',
    '#199e70',
    '#c98500',
    '#d55181',
    '#008300',
    '#9085e9',
    '#e66767'
];
export const STRATEGY_OVERFLOW_COLOR = '#71717a';

export const assignStrategyColors = (strategies: readonly Pick<PerformanceStrategy, 'strategyId'>[]): Map<number, string> =>
    new Map(strategies.map((strategy, index) => [strategy.strategyId, STRATEGY_COLOR_PALETTE[index] ?? STRATEGY_OVERFLOW_COLOR]));

export interface GlanceStat {
    label: string;
    pnl: number;
    returnPercent: number | null;
}

/** Snapshot-based history; the portfolio summary owns the single live daily gain/loss figure. */
export const buildGlanceStats = (days: readonly PerformanceDay[], today: string): GlanceStat[] => {
    const stat = (label: string, range: readonly PerformanceDay[]): GlanceStat => {
        const equity = computeEquityStats(range, 'trading', null);
        return { label, pnl: equity.pnl, returnPercent: equity.returnPercent };
    };

    const weekStart = dayjs(today, DATE_FORMAT).subtract(6, 'day').format(DATE_FORMAT);
    const monthStart = `${getMonthKey(today)}-01`;

    return [
        stat(
            '7 days',
            days.filter((day) => day.date >= weekStart)
        ),
        stat(
            'Month to date',
            days.filter((day) => day.date >= monthStart)
        ),
        stat('Since inception', days)
    ];
};
